<template>
  <Teleport to="body">
    <Transition name="export-status">
      <aside
        v-if="store.phase !== 'idle'"
        class="video-export-status"
        :class="{ 'video-export-status--dark': themeStore.isDarkMode }"
        aria-live="polite"
      >
        <div class="video-export-status__top">
          <div>
            <strong>{{ title }}</strong>
            <div class="video-export-status__file">{{ store.fileName }}</div>
          </div>
          <button
            v-if="store.phase !== 'exporting'"
            type="button"
            class="video-export-status__close"
            :aria-label="$t('Close')"
            @click="store.dismiss"
          >
            ×
          </button>
        </div>

        <template v-if="store.phase === 'exporting'">
          <div class="video-export-status__progress-row">
            <span>{{ $t('Video export is running in the background') }}</span>
            <span>{{ displayPct }}%</span>
          </div>
          <div class="video-export-status__track">
            <div
              class="video-export-status__fill"
              :style="{ width: displayPct + '%' }"
            ></div>
          </div>
          <div class="video-export-status__bottom">
            <small>{{ $t('Keep this browser tab open') }}</small>
            <button type="button" class="video-export-status__cancel" @click="store.cancel">
              {{ $t('Cancel export') }}
            </button>
          </div>
        </template>
      </aside>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useThemeStore } from '@stores/themeStore';
import { useVideoExportStore } from '@stores/videoExportStore';

export default defineComponent({
  name: 'VideoExportStatus',
  setup() {
    const store = useVideoExportStore();
    const themeStore = useThemeStore();
    const { t } = useI18n();
    const displayPct = computed(() => Math.min(100, Math.round(store.progressPct)));
    const title = computed(() => {
      switch (store.phase) {
        case 'completed':
          return '✓ ' + t('Video exported successfully');
        case 'cancelled':
          return t('Video export cancelled');
        case 'error':
          return t('Export failed');
        default:
          return t('Exporting video…');
      }
    });

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!store.isExporting) return;
      event.preventDefault();
      event.returnValue = '';
    };

    onMounted(() => window.addEventListener('beforeunload', warnBeforeUnload));
    onBeforeUnmount(() => window.removeEventListener('beforeunload', warnBeforeUnload));

    return {
      store,
      themeStore,
      displayPct,
      title,
    };
  },
});
</script>

<style scoped>
.video-export-status {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 1200;
  width: min(390px, calc(100vw - 36px));
  padding: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #1f2937;
  background: #fff;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.22);
}

@media (max-width: 1023px) {
  /* Sit above the fixed mobile tab bar (64px + safe area) instead of
     covering it — the widget outranks the bar on z-index. */
  .video-export-status {
    bottom: calc(84px + env(safe-area-inset-bottom));
  }
}

.video-export-status--dark {
  border-color: #4b5563;
  color: #f3f4f6;
  background: #1f2937;
}

.video-export-status__top,
.video-export-status__progress-row,
.video-export-status__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.video-export-status__file {
  max-width: 310px;
  margin-top: 2px;
  overflow: hidden;
  color: #64748b;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-export-status--dark .video-export-status__file {
  color: #cbd5e1;
}

.video-export-status__close {
  border: 0;
  color: inherit;
  background: transparent;
  font-size: 1.35rem;
  line-height: 1;
}

.video-export-status__progress-row {
  margin-top: 12px;
  font-size: 0.8rem;
}

.video-export-status__track {
  height: 9px;
  margin-top: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.24);
}

.video-export-status__fill {
  height: 100%;
  border-radius: inherit;
  background: #3b82f6;
  transition: width 0.25s ease;
}

.video-export-status__bottom {
  margin-top: 9px;
}

.video-export-status__bottom small {
  color: #64748b;
}

.video-export-status--dark .video-export-status__bottom small {
  color: #cbd5e1;
}

.video-export-status__cancel {
  border: 0;
  color: #dc2626;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
}

.export-status-enter-active,
.export-status-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.export-status-enter-from,
.export-status-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
